/**
 * Skeleton.tsx — Carbon Skeleton-anatomi (packages/carbon-react Skeleton)
 *
 * Replikert i native CSS Modules med --cds-* tokens.
 *
 * Carbon-standard som følges:
 * - Shimmer-animasjon (300ms, productive easing) over layer-bakgrunn
 * - Square (ingen radius)
 * - Tabell-variant: rader i samme struktur som DataTable (placeholder-celler)
 * - Tekst-variant: linjer i avtakende bredde
 * - aria-hidden + role="status" med sr-only-tekst for skjermlesere
 *
 * Bruk:
 *   <Skeleton variant="table" rows={5} columns={9} />
 *   <Skeleton variant="text" lines={3} />
 */

import styles from './skeleton.module.css';

interface SkeletonProps {
  variant: 'text' | 'table';
  /** Antall linjer (text) eller rader (table) */
  rows?: number;
  /** Antall kolonner (kun table) */
  columns?: number;
  /** Skjermleser-tekst (default "Laster innhold") */
  ariaLabel?: string;
}

export default function Skeleton({
  variant,
  rows = 3,
  columns = 4,
  ariaLabel = 'Laster innhold',
}: SkeletonProps) {
  return (
    <div className={styles.skeleton} role="status" aria-label={ariaLabel}>
      {variant === 'text' ? (
        // Tekst-variant: linjer i avtakende bredde (siste linje kortest)
        Array.from({ length: rows }, (_, i) => (
          <span
            key={i}
            className={styles.textLine}
            style={{ width: i === rows - 1 ? '60%' : '100%' }}
            aria-hidden="true"
          />
        ))
      ) : (
        // Tabell-variant: header-rad + N datarader med placeholder-celler
        <table className={styles.table} aria-hidden="true">
          <thead>
            <tr>
              {Array.from({ length: columns }, (_, i) => (
                <th key={i}>
                  <span className={styles.headerCell} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                {Array.from({ length: columns }, (_, c) => (
                  <td key={c}>
                    <span className={styles.cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
